import type { DashboardOverview, PageListItem, AIContextSummary, GSCDailyMetrics, TrendStatus } from '@ongrid/shared';

// API base URL - change for production
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8788';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  meta?: {
    total?: number;
    page?: number;
    pageSize?: number;
  };
}

interface PageListParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  trendStatus?: TrendStatus | null;
  search?: string;
}

/**
 * Fetch dashboard overview stats
 */
export async function fetchOverview(): Promise<DashboardOverview> {
  const response = await fetch(`${API_BASE}/api/overview`);
  const json: ApiResponse<DashboardOverview> = await response.json();

  if (!json.success || !json.data) {
    throw new Error(json.error || 'Failed to fetch overview');
  }

  return json.data;
}

/**
 * Fetch paginated page list
 */
export async function fetchPages(params: PageListParams = {}): Promise<{
  items: PageListItem[];
  total: number;
  page: number;
  pageSize: number;
}> {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', params.page.toString());
  if (params.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
  if (params.trendStatus) searchParams.set('trendStatus', params.trendStatus);
  if (params.search) searchParams.set('search', params.search);

  const response = await fetch(`${API_BASE}/api/pages?${searchParams}`);
  const json: ApiResponse<PageListItem[]> = await response.json();

  if (!json.success || !json.data) {
    throw new Error(json.error || 'Failed to fetch pages');
  }

  return {
    items: json.data,
    total: json.meta?.total ?? 0,
    page: json.meta?.page ?? 1,
    pageSize: json.meta?.pageSize ?? 50,
  };
}

/**
 * Fetch single page detail
 */
export async function fetchPageDetail(
  pageUrl: string
): Promise<{ summary: AIContextSummary; history: GSCDailyMetrics[] }> {
  const encodedUrl = encodeURIComponent(pageUrl);
  const response = await fetch(`${API_BASE}/api/pages/${encodedUrl}`);
  const json: ApiResponse<{ summary: AIContextSummary; history: GSCDailyMetrics[] }> =
    await response.json();

  if (!json.success || !json.data) {
    throw new Error(json.error || 'Failed to fetch page detail');
  }

  return json.data;
}

/**
 * Fetch top performers
 */
export async function fetchTopPerformers(limit: number = 10): Promise<PageListItem[]> {
  const response = await fetch(`${API_BASE}/api/top-performers?limit=${limit}`);
  const json: ApiResponse<PageListItem[]> = await response.json();

  if (!json.success || !json.data) {
    throw new Error(json.error || 'Failed to fetch top performers');
  }

  return json.data;
}

/**
 * Fetch pages needing attention
 */
export async function fetchNeedsAttention(limit: number = 10): Promise<PageListItem[]> {
  const response = await fetch(`${API_BASE}/api/needs-attention?limit=${limit}`);
  const json: ApiResponse<PageListItem[]> = await response.json();

  if (!json.success || !json.data) {
    throw new Error(json.error || 'Failed to fetch needs attention');
  }

  return json.data;
}

